import { useEffect, useMemo, useRef, useState } from 'react';
import { keyBy } from 'lodash';
import {
    Box,
    Button,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';

import { useTranslation } from 'helpers/languages/translations';
import {
    useDeleteProductClusterMutation,
    useDeleteShopClusterMutation,
    useEditProductClusterMutation,
    useEditShopClusterMutation,
    useGetClusterQuery,
} from 'core/API/clusterApi';
import RSelect from 'components/atoms/formInputs/RSelect';
import { TClusterPayload, TProductCluster } from 'pages/ClusterManagement/types';
import { getRandomHexColor } from 'utils/colors';
import ColorPicker from 'pages/Preferences/components/molecules/ColorsTab/ColorPicker';
import { TProductClusterViewer } from './ProductClusterContainer';
import ClusterCard from 'pages/ClusterManagement/ClusterCard';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import { isUpcEnabled } from 'shared';

type TShopClusterAddModal = {
    isOpen: boolean;
    currentProductCluster: TProductCluster | null;
    actionModalType: TProductClusterViewer['actionModalType'];
    onClose: () => void;
    setActionModalType: TProductClusterViewer['setActionModalType'];
};

const ProductClusterActionModal = ({
    isOpen,
    onClose,
    actionModalType,
    setActionModalType,
    currentProductCluster,
}: TShopClusterAddModal) => {
    const translate = useTranslation();

    const [selected, setSelected] = useState<(string | number)[]>([]);
    const { data: cData, isError, isFetching } = useGetClusterQuery();

    const [newClusters, setNewClusters] = useState<TClusterPayload[]>([]);
    const [currentCluster, setCurrentCluster] = useState<string | null>(null);
    const inputEanRef = useRef<any>(null);

    const [productClusterMutation, { isLoading: isEditLoading }] = useEditProductClusterMutation();
    const [deleteProductClusterMutation, { isLoading: isDeleteLoading }] = useDeleteProductClusterMutation();

    const isDelete = actionModalType === 'delete';

    const clusterData = useMemo(() => cData?.data || [], [cData]);
    const dataByLabel = useMemo(() => keyBy(clusterData, 'label'), [clusterData]);

    const newClustersByLabel = keyBy(newClusters, 'label');
    const clusterDataByLabel = keyBy(clusterData, 'label');

    const companyInfo = useGetCompanyInfoSelector();
    const isUpc = isUpcEnabled(companyInfo);

    const handleSubmit = async () => {
        const ean = currentProductCluster?.ean || inputEanRef.current.value;
        ean &&
            (await productClusterMutation(
                selected.map((s) => ({
                    label: `${s}`,
                    ean,
                    exist: !newClustersByLabel[s],
                    color: newClustersByLabel[s]?.color,
                })),
            ));

        setSelected([]);
        setCurrentCluster(null);

        onClose();
    };

    const handleDelete = async () => {
        currentProductCluster?.ean && (await deleteProductClusterMutation(currentProductCluster?.ean));

        setSelected([]);
        setCurrentCluster(null);

        onClose();
    };

    useEffect(() => {
        const nClusters: TClusterPayload[] = [];
        const newClusterByLabel = keyBy(newClusters, 'label');

        selected.forEach((s) => {
            if (!dataByLabel[s]) {
                nClusters.push({
                    label: s.toString(),
                    color: newClusterByLabel[s.toString()]?.color || getRandomHexColor(),
                });
            }
        });

        setNewClusters(nClusters);
    }, [selected]);

    useEffect(() => {
        setSelected(currentProductCluster?.clusters.map((c) => c.label) || []);
        setCurrentCluster(null);
    }, [currentProductCluster]);

    const setColor = (color: string, label: string) => {
        setNewClusters((c) => {
            const newC = [...c];
            const cToUpdate = newC.find((c) => c.label === label) || { color: '' };
            cToUpdate.color = color;
            return newC;
        });
    };

    const options =
        clusterData.map((cluster) => ({
            label: cluster.label,
            value: cluster.label,
            color: cluster.color,
        })) || [];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{translate(`productCluster.${actionModalType}Modal.header`)}</ModalHeader>
                <ModalBody>
                    {isDelete ? (
                        <Text>{translate('productCluster.delete.confirm')}</Text>
                    ) : (
                        <>
                            <Flex minH="40px" w="100%" alignItems="center" fontSize="sm">
                                <Text w="20%">{isUpc ? 'UPC' : 'EAN'}</Text>
                                <Flex flex="1">
                                    <Input
                                        disabled={!!currentProductCluster}
                                        size="sm"
                                        ref={inputEanRef}
                                        defaultValue={currentProductCluster?.ean || ''}
                                    />
                                </Flex>
                            </Flex>
                            <RSelect options={options} onChange={setSelected} selectedOptions={selected} isCreatable />
                            <Text>{translate('shopCluster.newClusters')}</Text>
                            <Flex direction="row" gap={2} h="320px">
                                <Flex
                                    gap={3}
                                    flexDirection="column"
                                    mt={2}
                                    maxH="100%"
                                    overflow="auto"
                                    p={1}
                                    flexGrow={1}
                                >
                                    {selected.map((key, idx) => (
                                        <Box
                                            onClick={() =>
                                                !!newClustersByLabel[key] &&
                                                setCurrentCluster(currentCluster === `${key}` ? null : `${key}`)
                                            }
                                            cursor={!!newClustersByLabel[key] ? 'pointer' : 'default'}
                                        >
                                            <ClusterCard
                                                cluster={clusterDataByLabel[key] || newClustersByLabel[key]}
                                                isNew={!!newClustersByLabel[key]}
                                                selected={currentCluster === `${key}`}
                                            />
                                        </Box>
                                    ))}
                                </Flex>
                                <Flex justifyContent="center">
                                    {newClustersByLabel[`${currentCluster}`] && (
                                        <ColorPicker
                                            colorToSubmit={{
                                                color: newClustersByLabel[`${currentCluster}`]?.color || '',
                                            }}
                                            changeColor={(c: string) => currentCluster && setColor(c, currentCluster)}
                                        />
                                    )}
                                </Flex>
                            </Flex>
                        </>
                    )}
                </ModalBody>
                <ModalFooter gap={2}>
                    {!isDelete && (
                        <Button colorScheme="red" onClick={() => setActionModalType('delete')}>
                            {translate('productCluster.delete')}
                        </Button>
                    )}
                    <Box flexGrow={1} />
                    <Button colorScheme="gray" onClick={onClose}>
                        {translate('productCluster.cancel')}
                    </Button>
                    <Button colorScheme="blue" onClick={isDelete ? handleDelete : handleSubmit}>
                        {translate(`productCluster.${actionModalType}Modal.onSubmit`)}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ProductClusterActionModal;
